import React from 'react';
import { connect } from 'react-redux';
import { Button, Popconfirm, Divider, message } from 'antd';
import * as HttpStatus from 'http-status-codes';
import Card from '../../../../../components/Card/Card';
import Modal from '../../../../../components/Modal/Modal';
import Table from '../../../../../components/Table/Table';
import InfoBox from '../../../../../components/InfoBox/InfoBox';
import ModuleEmptyState from '../../../../../components/ModuleEmptyState';
import TeamContactAdd from './TeamContactAdd';
import TeamContactEdit from './TeamContactEdit';
import ContactEdit from '../../../../Organisation/scenes/Contact/components/ContactEdit';
import { createTeamContact, updateTeamContact, deleteTeamContact } from '../TeamAction';
import { planIdSelector } from '../../Plan/PlanSelectors';
import { MSG_SAVE_SUCCESS, MSG_ERROR, MSG_PAYMENT_REQUIRED } from '../../../../../constants';
import { getApiErrorMessage, parseHttpError } from '../../../../../utils/httpError';

const TeamType = ({
  planId,
  team,
  teamGuidance = null,
  teamDetail = null,
  createTeamContact,
  updateTeamContact,
  deleteTeamContact,
}) => {
  const { teamId, teamType, teamContacts = [] } = team || {};
  const teamName = teamType?.name || 'Team';
  const teamContactList = (teamContacts || [])
    .filter((obj) => obj && obj.contact)
    .map((obj) => ({ ...obj.contact, designation: obj.designation }));
  const hasMembers = teamContactList.length > 0;

  const onCreateTeamContact = async (data) => {
    try {
      await createTeamContact(planId, teamId, data);
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onUpdateTeamContact = async (contactId, data) => {
    try {
      await updateTeamContact(planId, teamId, contactId, data);
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onDeleteTeamContact = async (contactId) => {
    try {
      await deleteTeamContact(planId, teamId, contactId);
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const addMemberModal = (button) => (
    <Modal title="Add Member" toggle={(show) => button(show)}>
      {(hide) => <TeamContactAdd teamInfo={team} onCreate={onCreateTeamContact} close={hide} />}
    </Modal>
  );

  const addNewContactModal = (button) => (
    <Modal explanation toggle={(show) => button(show)}>
      {(hide, setExplanation) => (
        <ContactEdit
          addToTeam={(data) => onCreateTeamContact(data)}
          close={hide}
          onExplanationChange={setExplanation}
        />
      )}
    </Modal>
  );

  const columns = [
    { key: 'firstName', title: 'First Name', dataIndex: 'firstName' },
    { key: 'lastName', title: 'Last Name', dataIndex: 'lastName' },
    { key: 'jobTitle', title: 'Job Title', dataIndex: 'jobTitle' },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      render: (text) =>
        text ? (
          <a className="team-link" href={`mailto:${text}`}>
            {text}
          </a>
        ) : null,
    },
    {
      key: 'phone',
      title: 'Phone',
      dataIndex: 'phone',
      render: (text) =>
        text ? (
          <a className="team-link" href={`tel:${text}`}>
            {text}
          </a>
        ) : null,
    },
    {
      key: 'mobile',
      title: 'Mobile',
      dataIndex: 'mobile',
      render: (text) =>
        text ? (
          <a className="team-link" href={`tel:${text}`}>
            {text}
          </a>
        ) : null,
    },
    { key: 'designation', title: 'Designation', dataIndex: 'designation' },
    {
      key: 'action',
      width: 115,
      render: (text, record) => (
        <span>
          <Modal
            title={`${record.firstName} ${record.lastName}`}
            toggle={(show) => <Button type="link" icon="edit" className="team-link" onClick={show} />}
          >
            {(hide) => <TeamContactEdit teamContactInfo={record} onUpdate={onUpdateTeamContact} close={hide} />}
          </Modal>
          <Divider type="vertical" />
          <Popconfirm title="Sure to delete?" onConfirm={() => onDeleteTeamContact(record.contactId)}>
            <Button type="link" icon="delete" className="team-link" />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <Card color="#5BC2E7" title={`Team - ${teamName}`}>
      {(teamGuidance || teamDetail) && (
        <InfoBox color="#5BC2E7" className="team-type-guidance" guidance={teamGuidance} message={teamDetail} />
      )}
      <Table
        title="Team Contacts"
        columns={columns}
        dataSource={teamContactList}
        rowKey="contactId"
        breakpoint={1024}
        moduleEmpty={
          <ModuleEmptyState
            moduleTitle={`Team - ${teamName}`}
            embedded
            headline="No team members yet"
            description="Assign contacts who will serve on this team. Every team must have at least one contact assigned."
            tip="Tip: select existing organisation contacts, or create a new contact and add them in one step."
            iconType="team"
          >
            {addMemberModal((show) => (
              <Button type="primary" className="module-empty-state__btn-primary" htmlType="button" onClick={show}>
                Add First Member
              </Button>
            ))}
            {addNewContactModal((show) => (
              <Button type="default" className="module-empty-state__btn-secondary" htmlType="button" onClick={show}>
                Add new contact
              </Button>
            ))}
          </ModuleEmptyState>
        }
      />
      {hasMembers ? (
        <div className="btn-container">
          {addNewContactModal((show) => (
            <Button type="default" onClick={show} className="team-btn">
              Add new contact
            </Button>
          ))}
          {addMemberModal((show) => (
            <Button type="primary" icon="contacts" onClick={show} className="team-btn">
              Add Member
            </Button>
          ))}
        </div>
      ) : null}
    </Card>
  );
};

const mapStateToProps = (state) => ({
  planId: planIdSelector(state),
});

const mapDispatchToProps = { createTeamContact, updateTeamContact, deleteTeamContact };

export default connect(mapStateToProps, mapDispatchToProps)(TeamType);
